/**
 * The tenant-facing vocabulary of the Create Campaign flow (the four steps of
 * the mockup: what are you promoting, creative mode, create for, creative
 * direction), and its mapping onto structured domain concepts.
 *
 * This is the "configuration registry, not scattered labels" rule (spec §7,
 * §30): the objective/mode/channel/style catalogs live here as data, so the
 * form renders from the registry and the planner reads the SAME registry. A
 * label never gets typed into a component and then disagreed with by a prompt
 * builder. Adding a lifecycle objective ("Ends in 2 Hours") is a row here, not
 * an edit in three files.
 *
 * Channels are deliberately NOT providers and NOT one master creative cropped
 * six ways (spec §9, §96). A channel maps to one or more output FORMATS, each
 * with its own canvas and its own template layout downstream.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Step 1 - Campaign objective (what are you promoting?)
// ---------------------------------------------------------------------------

export const CAMPAIGN_OBJECTIVES = [
  'AUCTION_ANNOUNCEMENT',
  'CONSIGNMENTS_OPEN',
  'BIDDING_OPEN',
  'FEATURED_LOTS',
  'CLOSING_TONIGHT',
  'SALE_RESULTS',
  'INDIVIDUAL_LOT',
  'CUSTOM',
] as const;
export const CampaignObjectiveSchema = z.enum(CAMPAIGN_OBJECTIVES);
export type CampaignObjective = z.infer<typeof CampaignObjectiveSchema>;

export interface CampaignObjectiveDef {
  key: CampaignObjective;
  /** The tenant-facing card label. */
  label: string;
  /** The one-line helper under the label. */
  blurb: string;
  /** Whether this objective needs the tenant to pick specific lots (spec §10,
   *  Featured Lots). INDIVIDUAL_LOT requires exactly one; FEATURED_LOTS a set. */
  lotSelection: 'none' | 'one' | 'many';
  /** Whether the creative's SUBJECT may be AI-generated. An objective that
   *  represents a specific real consignment is REAL_SUBJECT_REQUIRED: the horse
   *  shown must be that horse, from real media - AI may never generate, modify
   *  or "improve" it. This is a domain rule, not a prompt hint (ADR-56 cut 7). */
  subjectPolicy: SubjectPolicy;
  /** Lifecycle objectives (spec §7) are gated behind a flag until built. */
  lifecycle?: boolean;
}

export const CAMPAIGN_OBJECTIVE_DEFS: Readonly<Record<CampaignObjective, CampaignObjectiveDef>> = {
  AUCTION_ANNOUNCEMENT: {
    key: 'AUCTION_ANNOUNCEMENT',
    label: 'Auction Announcement',
    blurb: 'Announce the sale, its dates and where to bid.',
    lotSelection: 'none',
    subjectPolicy: 'GENERATED_SUBJECT_ALLOWED',
  },
  CONSIGNMENTS_OPEN: {
    key: 'CONSIGNMENTS_OPEN',
    label: 'Consignments Open',
    blurb: 'Invite consignors while the catalog is still filling.',
    lotSelection: 'none',
    subjectPolicy: 'GENERATED_SUBJECT_ALLOWED',
  },
  BIDDING_OPEN: {
    key: 'BIDDING_OPEN',
    label: 'Bidding Open',
    blurb: 'Bidding is live - send buyers to the sale.',
    lotSelection: 'none',
    subjectPolicy: 'GENERATED_SUBJECT_ALLOWED',
  },
  FEATURED_LOTS: {
    key: 'FEATURED_LOTS',
    label: 'Featured Horses',
    blurb: 'Spotlight a handful of strong lots.',
    lotSelection: 'many',
    subjectPolicy: 'REAL_SUBJECT_REQUIRED',
  },
  CLOSING_TONIGHT: {
    key: 'CLOSING_TONIGHT',
    label: 'Closing Tonight',
    blurb: 'Urgency for the final hours.',
    lotSelection: 'none',
    subjectPolicy: 'GENERATED_SUBJECT_ALLOWED',
  },
  SALE_RESULTS: {
    key: 'SALE_RESULTS',
    label: 'Sale Results',
    blurb: 'Recap what sold once the sale has closed.',
    // A recap plus one creative per top-selling horse (hardening D); the
    // automatic pick is by hammer price, from the settlement record.
    lotSelection: 'many',
    subjectPolicy: 'REAL_SUBJECT_REQUIRED',
  },
  INDIVIDUAL_LOT: {
    key: 'INDIVIDUAL_LOT',
    label: 'Individual Horse / Lot',
    blurb: 'One lot, its hip number and its photos.',
    lotSelection: 'one',
    subjectPolicy: 'REAL_SUBJECT_REQUIRED',
  },
  CUSTOM: {
    key: 'CUSTOM',
    label: 'Custom Campaign',
    blurb: 'Start from a blank objective.',
    lotSelection: 'none',
    subjectPolicy: 'GENERATED_SUBJECT_ALLOWED',
  },
};

// ---------------------------------------------------------------------------
// Step 2 - Visual strategy and the subject-policy rule (ADR-56 cut 7)
//
// Two orthogonal axes, not one conflated "mode":
//   VisualStrategy - HOW the creative visual is produced.
//   SubjectPolicy  - WHETHER the subject may be generated (a rule of the
//                    objective; the truth layers are ALWAYS rendered by
//                    HipNumber regardless of strategy).
// ---------------------------------------------------------------------------

export const VISUAL_STRATEGIES = ['BRAND_TREATMENT', 'REAL_MEDIA', 'AI_CREATIVE'] as const;
export const VisualStrategySchema = z.enum(VISUAL_STRATEGIES);
export type VisualStrategy = z.infer<typeof VisualStrategySchema>;

export const SUBJECT_POLICIES = ['GENERATED_SUBJECT_ALLOWED', 'REAL_SUBJECT_REQUIRED'] as const;
export const SubjectPolicySchema = z.enum(SUBJECT_POLICIES);
export type SubjectPolicy = z.infer<typeof SubjectPolicySchema>;

export interface VisualStrategyDef {
  key: VisualStrategy;
  label: string;
  blurb: string;
  /** True only for AI_CREATIVE: it spends metered external generation. The
   *  other strategies are near-zero marginal processing cost. */
  usesGenerativeProvider: boolean;
}

export const VISUAL_STRATEGY_DEFS: Readonly<Record<VisualStrategy, VisualStrategyDef>> = {
  BRAND_TREATMENT: {
    key: 'BRAND_TREATMENT',
    label: 'Brand Treatment',
    blurb: 'Your brand, rendered - colour, type, gradient or pattern. Included.',
    usesGenerativeProvider: false,
  },
  REAL_MEDIA: {
    key: 'REAL_MEDIA',
    label: 'Your Photos',
    blurb: 'Real photos from your media library - the venue, the horses, past sales. Included.',
    usesGenerativeProvider: false,
  },
  AI_CREATIVE: {
    key: 'AI_CREATIVE',
    label: 'AI Creative',
    blurb: 'AI generates the whole creative visual - character, scene, seasonal art. Uses AI credits.',
    usesGenerativeProvider: true,
  },
};

/** The subject policy an objective imposes (a domain rule). */
export function subjectPolicyForObjective(objective: CampaignObjective): SubjectPolicy {
  return CAMPAIGN_OBJECTIVE_DEFS[objective].subjectPolicy;
}

/**
 * The hard rule (ADR-56 cut 7): AI_CREATIVE may generate the creative visual
 * ONLY when the objective allows a generated subject. A REAL_SUBJECT_REQUIRED
 * objective (a specific consignment) must use real media - AI can never stand in
 * for, or alter, a real horse. BRAND_TREATMENT and REAL_MEDIA are always legal.
 */
export function visualStrategyAllowed(strategy: VisualStrategy, subjectPolicy: SubjectPolicy): boolean {
  if (strategy === 'AI_CREATIVE') return subjectPolicy === 'GENERATED_SUBJECT_ALLOWED';
  return true;
}

/** The strategy the Create Campaign form opens on for an objective: real media
 *  when a real subject is required, otherwise the included brand treatment. AI
 *  Creative is always an explicit, cost-showing opt-in - never the default. */
export function defaultVisualStrategy(objective: CampaignObjective): VisualStrategy {
  return subjectPolicyForObjective(objective) === 'REAL_SUBJECT_REQUIRED' ? 'REAL_MEDIA' : 'BRAND_TREATMENT';
}

/** Where an approved AI creative may be reused (spec §43, §71). */
/**
 * How much a Sale Results campaign discloses (ADR-56 hardening D). The public
 * catalog already shows each lot's status, so "what sold" is never more than
 * the storefront says; hammer prices are the tenant's explicit choice, frozen
 * into the campaign. Buyer identity is never disclosed by any option.
 */
export const RESULTS_DISCLOSURES = ['SOLD_ONLY', 'WITH_PRICES'] as const;
export const ResultsDisclosureSchema = z.enum(RESULTS_DISCLOSURES);
export type ResultsDisclosure = z.infer<typeof ResultsDisclosureSchema>;
export const RESULTS_DISCLOSURE_DEFS: Readonly<Record<ResultsDisclosure, { label: string; blurb: string }>> = {
  SOLD_ONLY: { label: 'What sold', blurb: 'Sold horses and the sale totals. No prices, as on the public catalog.' },
  WITH_PRICES: { label: 'With hammer prices', blurb: 'Adds each featured horse\'s hammer price and the sale gross.' },
};

export const VISUAL_REUSE_SCOPES = ['CAMPAIGN', 'AUCTION_LIBRARY', 'TENANT_LIBRARY'] as const;
export const VisualReuseScopeSchema = z.enum(VISUAL_REUSE_SCOPES);
export type VisualReuseScope = z.infer<typeof VisualReuseScopeSchema>;

// ---------------------------------------------------------------------------
// Output formats - the canvas a creative is actually produced at
// ---------------------------------------------------------------------------

export const CREATIVE_FORMATS = [
  'FEED_4_5',
  'SQUARE_1_1',
  'VERTICAL_9_16',
  'LANDSCAPE_16_9',
  'WEBSITE_HERO',
  'EMAIL_HEADER',
] as const;
export const CreativeFormatSchema = z.enum(CREATIVE_FORMATS);
export type CreativeFormat = z.infer<typeof CreativeFormatSchema>;

export interface CreativeFormatDef {
  key: CreativeFormat;
  label: string;
  media: 'IMAGE' | 'VIDEO';
  width: number;
  height: number;
  /** Human aspect label, e.g. "4:5". */
  aspect: string;
}

export const CREATIVE_FORMAT_DEFS: Readonly<Record<CreativeFormat, CreativeFormatDef>> = {
  FEED_4_5: { key: 'FEED_4_5', label: 'Feed Portrait', media: 'IMAGE', width: 1080, height: 1350, aspect: '4:5' },
  SQUARE_1_1: { key: 'SQUARE_1_1', label: 'Square', media: 'IMAGE', width: 1080, height: 1080, aspect: '1:1' },
  VERTICAL_9_16: { key: 'VERTICAL_9_16', label: 'Vertical Video', media: 'VIDEO', width: 1080, height: 1920, aspect: '9:16' },
  LANDSCAPE_16_9: { key: 'LANDSCAPE_16_9', label: 'Landscape Video', media: 'VIDEO', width: 1920, height: 1080, aspect: '16:9' },
  WEBSITE_HERO: { key: 'WEBSITE_HERO', label: 'Website Hero', media: 'IMAGE', width: 1920, height: 600, aspect: '16:5' },
  EMAIL_HEADER: { key: 'EMAIL_HEADER', label: 'Email Header', media: 'IMAGE', width: 1200, height: 400, aspect: '3:1' },
};

// ---------------------------------------------------------------------------
// Step 3 - Channels (create for)
// ---------------------------------------------------------------------------

export const MARKETING_CHANNELS = [
  'FACEBOOK_FEED',
  'INSTAGRAM_FEED',
  'REELS_TIKTOK',
  'YOUTUBE',
  'WEBSITE_HERO',
  'EMAIL_SMS',
] as const;
export const MarketingChannelSchema = z.enum(MARKETING_CHANNELS);
export type MarketingChannel = z.infer<typeof MarketingChannelSchema>;

export interface MarketingChannelDef {
  key: MarketingChannel;
  label: string;
  /** The formats this channel produces. A channel can fan out to more than one
   *  (email wants a header image AND copy); each format reflows its own layout,
   *  never a crop of a master (spec §9). Ordered: primary format first. */
  formats: readonly CreativeFormat[];
}

export const MARKETING_CHANNEL_DEFS: Readonly<Record<MarketingChannel, MarketingChannelDef>> = {
  FACEBOOK_FEED: { key: 'FACEBOOK_FEED', label: 'Facebook Feed', formats: ['FEED_4_5'] },
  INSTAGRAM_FEED: { key: 'INSTAGRAM_FEED', label: 'Instagram Feed', formats: ['FEED_4_5', 'SQUARE_1_1'] },
  REELS_TIKTOK: { key: 'REELS_TIKTOK', label: 'Reels + TikTok', formats: ['VERTICAL_9_16'] },
  YOUTUBE: { key: 'YOUTUBE', label: 'YouTube', formats: ['LANDSCAPE_16_9'] },
  WEBSITE_HERO: { key: 'WEBSITE_HERO', label: 'Website Hero', formats: ['WEBSITE_HERO'] },
  EMAIL_SMS: { key: 'EMAIL_SMS', label: 'Email + SMS', formats: ['EMAIL_HEADER'] },
};

/**
 * The set of distinct formats a chosen set of channels needs, de-duplicated
 * and in a stable order (Instagram and Facebook both wanting 4:5 must not
 * plan two identical renders). This is the "format selection" the planner
 * runs, kept pure so it is unit-testable (spec §86).
 */
export function formatsForChannels(channels: readonly MarketingChannel[]): CreativeFormat[] {
  const seen = new Set<CreativeFormat>();
  const out: CreativeFormat[] = [];
  for (const key of CREATIVE_FORMATS) {
    for (const ch of channels) {
      if (MARKETING_CHANNEL_DEFS[ch].formats.includes(key) && !seen.has(key)) {
        seen.add(key);
        out.push(key);
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Step 4 - Visual style (creative direction)
// ---------------------------------------------------------------------------

export const VISUAL_STYLES = [
  'PREMIUM_WESTERN_EDITORIAL',
  'RUGGED_WESTERN',
  'CLEAN_MODERN_LUXURY',
  'HIGH_ENERGY_SALE_DAY',
  'RUSTIC_RANCH',
  'MINIMAL_PREMIUM',
  'CINEMATIC',
  'TRADITIONAL_LIVESTOCK_SALE',
  'CUSTOM_BRAND_STYLE',
] as const;
export const VisualStyleSchema = z.enum(VISUAL_STYLES);
export type VisualStyle = z.infer<typeof VisualStyleSchema>;

export interface VisualStyleDef {
  key: VisualStyle;
  label: string;
  /** Structured creative-direction keywords the prompt planner consumes. NOT a
   *  raw prompt (spec §10, §42): the planner assembles the final provider
   *  prompt from these plus brand, truth, template and safe zones. */
  keywords: readonly string[];
}

export const VISUAL_STYLE_DEFS: Readonly<Record<VisualStyle, VisualStyleDef>> = {
  PREMIUM_WESTERN_EDITORIAL: { key: 'PREMIUM_WESTERN_EDITORIAL', label: 'Premium Western Editorial', keywords: ['editorial', 'premium', 'warm light', 'shallow depth of field'] },
  RUGGED_WESTERN: { key: 'RUGGED_WESTERN', label: 'Rugged Western', keywords: ['rugged', 'dust', 'golden hour', 'ranch'] },
  CLEAN_MODERN_LUXURY: { key: 'CLEAN_MODERN_LUXURY', label: 'Clean Modern Luxury', keywords: ['clean', 'modern', 'luxury', 'negative space'] },
  HIGH_ENERGY_SALE_DAY: { key: 'HIGH_ENERGY_SALE_DAY', label: 'High-Energy Sale Day', keywords: ['high energy', 'bold', 'action'] },
  RUSTIC_RANCH: { key: 'RUSTIC_RANCH', label: 'Rustic Ranch', keywords: ['rustic', 'wood', 'natural', 'homestead'] },
  MINIMAL_PREMIUM: { key: 'MINIMAL_PREMIUM', label: 'Minimal Premium', keywords: ['minimal', 'restrained', 'premium', 'lots of space'] },
  CINEMATIC: { key: 'CINEMATIC', label: 'Cinematic', keywords: ['cinematic', 'dramatic light', 'film grain', 'anamorphic'] },
  TRADITIONAL_LIVESTOCK_SALE: { key: 'TRADITIONAL_LIVESTOCK_SALE', label: 'Traditional Livestock Sale', keywords: ['traditional', 'classic', 'trustworthy'] },
  CUSTOM_BRAND_STYLE: { key: 'CUSTOM_BRAND_STYLE', label: 'Custom Brand Style', keywords: [] },
};

// ---------------------------------------------------------------------------
// Featured-lot selection strategy (spec §10)
// ---------------------------------------------------------------------------

/**
 * How the campaign chooses which lots to feature. Selection uses explicit
 * business signals and media availability only - never an AI judgement about
 * animal quality (spec §10, §96).
 */
export const FeaturedLotSelectionSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('AUTO') }).strict(),
  z.object({ mode: z.literal('TOP_BY_VIEWS'), count: z.number().int().min(1).max(24) }).strict(),
  z.object({ mode: z.literal('TOP_BY_WATCHLIST'), count: z.number().int().min(1).max(24) }).strict(),
  z.object({ mode: z.literal('MANUAL'), lotIds: z.array(z.string().min(1)).min(1) }).strict(),
  z.object({ mode: z.literal('CONSIGNOR'), consignorId: z.string().min(1) }).strict(),
  z.object({ mode: z.literal('CATEGORY'), category: z.string().min(1) }).strict(),
  z.object({ mode: z.literal('HIP_RANGE'), from: z.number().int().min(1), to: z.number().int().min(1) }).strict(),
]);
export type FeaturedLotSelection = z.infer<typeof FeaturedLotSelectionSchema>;
